<template>
  <Teleport :to="portalRootElementSelector">
    <ModalRoot v-model="showPointsModal" @close="onModalClosed">
      <template #body="{ close }">
        <ModalBodyV2
          :aria-labelledby="modalTitleId"
          class="items-center !max-w-md !px-4 sm:!px-7 !gap-6"
          @close="close"
        >
          <!-- Header -->
          <div class="flex flex-col items-center gap-2 text-center">
            <div
              class="size-14 rounded-full bg-fill-brand/10 flex items-center justify-center"
            >
              <Icon icon="mdi:star-circle" class="text-3xl text-fill-brand" />
            </div>
            <h2 :id="modalTitleId" class="font-bold text-xl text-text">
              {{ $t("channel.gating.points.modal.title") }}
            </h2>
            <p class="text-text-secondary text-base">
              {{ modalSubtitle }}
            </p>
          </div>

          <!-- Points display -->
          <div
            v-if="!isLoading"
            role="status"
            aria-live="polite"
            class="w-full space-y-3"
          >
            <!-- Required points -->
            <div
              class="flex items-center justify-between p-4 rounded-xl bg-surface-secondary"
            >
              <span class="text-text-secondary text-sm font-medium">
                {{ $t("channel.gating.points.modal.required") }}
              </span>
              <span class="text-text font-bold text-lg">
                {{ requiredPoints.toLocaleString() }}
              </span>
            </div>

            <!-- Current points -->
            <div
              class="flex items-center justify-between p-4 rounded-xl bg-surface-secondary"
            >
              <span class="text-text-secondary text-sm font-medium">
                {{ $t("channel.gating.points.modal.current") }}
              </span>
              <span
                class="font-bold text-lg"
                :class="hasEnoughPoints ? 'text-green-500' : 'text-red-400'"
              >
                {{ currentPoints.toLocaleString() }}
              </span>
            </div>

            <!-- Time window context -->
            <p
              v-if="timeWindowLabel"
              class="text-text-secondary text-xs text-center"
            >
              {{ timeWindowLabel }}
            </p>

            <!-- Community context -->
            <p
              v-if="communityName"
              class="text-text-secondary text-xs text-center"
            >
              {{
                $t("channel.gating.points.modal.communityContext", {
                  community: communityName,
                })
              }}
            </p>
          </div>

          <!-- Loading skeleton -->
          <div v-else class="w-full space-y-3 animate-pulse">
            <div class="h-14 rounded-xl bg-surface-secondary" />
            <div class="h-14 rounded-xl bg-surface-secondary" />
          </div>

          <!-- Earn More Points CTA -->
          <ButtonV2
            variant="primary"
            size="lg"
            class="self-stretch"
            @click="onEarnMorePoints(close)"
          >
            {{ $t("channel.gating.points.modal.earnMore") }}
          </ButtonV2>

          <!-- Dismiss -->
          <ButtonV2
            variant="subtle"
            size="lg"
            class="self-stretch !font-sans !font-semibold"
            @click="close"
          >
            {{ $t("channel.accessModal.later") }}
          </ButtonV2>
        </ModalBodyV2>
      </template>
    </ModalRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { Icon } from "@iconify/vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ModalBodyV2 from "@/components/ui/modal/modal-body-v2.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { portalRootElementSelector } from "@/constants/ui";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/user.store";
import type { AuthorizationRuleForGating } from "@/features/gating/use-gating-permissions";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { ConversionType } from "@/generated/graphql";

const { t } = useI18n();
const userStore = useUserStore();
const { emitChannelEvent } = useChannelEvents();

const emit = defineEmits<{
  "points-earned": [];
  "upgrade-cancelled": [];
}>();

// Internal state
const showPointsModal = ref(false);
const requestedRule = ref<AuthorizationRuleForGating | null>(null);
const isLoading = ref(false);

// Register with the just-one-open-modal system
useJustOneOpenModal(showPointsModal);

// Unique ID for aria-labelledby
const modalTitleId = "points-gating-modal-title";

// Derived data from the requested rule
const criterion = computed(() => requestedRule.value?.points_criterion ?? null);

const requiredPoints = computed(() => criterion.value?.min_points ?? 0);

const currentPoints = computed(() => criterion.value?.user_current_points ?? 0);

const hasEnoughPoints = computed(
  () => currentPoints.value >= requiredPoints.value,
);

const communityName = computed(() => criterion.value?.community_name ?? null);

const timeWindowLabel = computed(() => {
  const window = criterion.value?.time_window;
  if (!window || window === "all_time") return null;

  return t(`channel.gating.points.timeWindows.${window}`);
});

const modalSubtitle = computed(() => {
  const deficit = requiredPoints.value - currentPoints.value;
  if (deficit <= 0) {
    return t("channel.gating.points.modal.subtitleMet");
  }
  return t("channel.gating.points.modal.subtitle", {
    points: deficit.toLocaleString(),
  });
});

// Public methods (exposed to parent via template ref)
function showUpgradeFlow(rule?: AuthorizationRuleForGating | null) {
  requestedRule.value = rule ?? null;
  showPointsModal.value = true;

  // Fire gating_denied_points Amplitude event on show
  userStore.trackAnalyticsAction({
    appActionName: "GATING_DENIED_POINTS",
    actionMetadata: {
      requiredPoints: rule?.points_criterion?.min_points ?? 0,
      currentPoints: rule?.points_criterion?.user_current_points ?? 0,
      timeWindow: rule?.points_criterion?.time_window ?? "all_time",
      communityId: rule?.points_criterion?.community_id ?? null,
    },
  });
}

// Event handlers
function onEarnMorePoints(closeModal: () => void) {
  closeModal();
  requestedRule.value = null;

  // Emit upgrade finished event so the gating flow can continue
  emitChannelEvent({
    type: ChannelEventType.UPGRADE_FINISHED,
    conversionType: ConversionType.Free,
  });
}

function onModalClosed() {
  showPointsModal.value = false;
  requestedRule.value = null;
  emit("upgrade-cancelled");
}

// Expose methods to parent
defineExpose({
  showUpgradeFlow,
});
</script>
